import {
  getTaxDecimal,
  isDtoolsSiQuoteLocked,
  isQuoteDraftEditable,
  QUERY_KEYS,
  updateRecord,
  updateQuoteDetail,
  useDeleteQuoteDetails,
  useQuoteDetailsQuery,
  useQuoteDetailsSummary,
  useQuoteQuery,
  useStoredSuppliesDetailsQuery,
} from '@/lib/api'
import { isQueryLoaded } from '@/lib/plugins/plugin-readiness'
import { usePluginMutation } from '@/lib/plugins/use-plugin-mutation'
import { usePluginQueryKeysToInvalidateStore } from '@/lib/store'
import { getLockedSuppliesRates, getStoredSuppliesAmounts, getSuppliesSyncPlan } from '@/lib/supplies-policy'
import { QuoteType } from '@/lib/types'
import { PRODUCT_ID_MAP } from '@/lib/utils'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import Decimal from 'decimal.js'
import { memo, useCallback, useEffect, useMemo } from 'react'
import type { QuoteData } from '@/lib/api/quotes'

export const SuppliesPlugin = memo(function SuppliesPlugin() {
  const queryClient = useQueryClient()
  const { data: quote, dataUpdatedAt: quoteUpdatedAt, isError: quoteIsError, isFetching: quoteIsFetching } = useQuoteQuery()
  const keepSuppliesUnchanged =
    quote?.dsy_type === QuoteType.ChangeOrder && quote.dsy_keepsuppliesunchanged === true
  const {
    data: quoteDetails,
    dataUpdatedAt: quoteDetailsUpdatedAt,
    isFetching: quoteDetailsAreFetching,
  } = useQuoteDetailsQuery({
    onlySummaryDetails: 'all',
  })
  const { dataUpdatedAt: sourceQuoteDetailsUpdatedAt, isFetching: sourceQuoteDetailsAreFetching } =
    useQuoteDetailsQuery()
  const {
    data: summary,
    dataUpdatedAt: summaryUpdatedAt,
    isFetching: summaryIsFetching,
  } = useQuoteDetailsSummary()
  const {
    data: previousQuote,
    isError: previousQuoteIsError,
    isFetching: previousQuoteIsFetching,
  } = useQuoteQuery({ previousQuote: keepSuppliesUnchanged })
  const {
    data: previousSuppliesDetails,
    dataUpdatedAt: previousSuppliesDetailsUpdatedAt,
    isError: previousSuppliesDetailsIsError,
    isFetching: previousSuppliesDetailsAreFetching,
  } = useStoredSuppliesDetailsQuery({
    quoteId: previousQuote?.quoteid,
    enabled: keepSuppliesUnchanged,
  })
  const { mutateAsync: deleteQuoteDetails } = useDeleteQuoteDetails()
  const isUpdatingSuppliesPolicy =
    useIsMutating({ mutationKey: ['supplies-policy', 'toggle', quote?.quoteid] }) > 0

  const addQueryKeys = usePluginQueryKeysToInvalidateStore((state) => state.addQueryKeys)

  const isDraft = isQuoteDraftEditable(quote)
  const isDtoolsSiLocked = isDtoolsSiQuoteLocked(quote)
  const sourceQueriesLoaded =
    isQueryLoaded(quoteDetailsUpdatedAt) &&
    isQueryLoaded(sourceQuoteDetailsUpdatedAt) &&
    isQueryLoaded(summaryUpdatedAt) &&
    !quoteIsError &&
    !quoteIsFetching &&
    !quoteDetailsAreFetching &&
    !sourceQuoteDetailsAreFetching &&
    !summaryIsFetching &&
    (!keepSuppliesUnchanged ||
      (previousQuote?.quoteid !== quote?.quoteid &&
        !previousQuoteIsError &&
        !previousSuppliesDetailsIsError &&
        !previousQuoteIsFetching &&
        !previousSuppliesDetailsAreFetching &&
        isQueryLoaded(previousSuppliesDetailsUpdatedAt)))

  const previousSupplies = useMemo(
    () => getStoredSuppliesAmounts(previousSuppliesDetails ?? []),
    [previousSuppliesDetails]
  )

  const lockedRates = useMemo(
    () => (keepSuppliesUnchanged && previousSupplies ? getLockedSuppliesRates(summary, previousSupplies) : null),
    [keepSuppliesUnchanged, previousSupplies, summary]
  )

  const desiredSupplies = useMemo(
    () =>
      keepSuppliesUnchanged
        ? previousSupplies
        : {
            price: summary.supplies.price,
            cost: summary.supplies.cost,
          },
    [keepSuppliesUnchanged, previousSupplies, summary.supplies.cost, summary.supplies.price]
  )

  const suppliesQuoteDetail = useMemo(
    () => quoteDetails?.find((detail) => detail.productid.productid === PRODUCT_ID_MAP['SUPPLIES']),
    [quoteDetails]
  )

  const isDirty = useCallback(() => {
    if (!quote || !quoteDetails || !desiredSupplies || !suppliesQuoteDetail || !isDraft || isDtoolsSiLocked)
      return false
    if (keepSuppliesUnchanged && !lockedRates) return false

    const newPrice = new Decimal(desiredSupplies.price).toDecimalPlaces(2)
    const newCost = new Decimal(desiredSupplies.cost).toDecimalPlaces(2)
    const newTax = getTaxDecimal(newPrice, 1)
    return getSuppliesSyncPlan({
      currentRates: {
        priceRate: quote.dsy_suppliespricerate,
        costRate: quote.dsy_suppliescostrate,
      },
      desiredRates: keepSuppliesUnchanged ? lockedRates : null,
      currentDetail: {
        price: suppliesQuoteDetail.priceperunit,
        cost: suppliesQuoteDetail.msdyn_costpriceperunit,
        costAmount: suppliesQuoteDetail.msdyn_costamount,
        tax: suppliesQuoteDetail.tax,
      },
      desiredDetail: {
        price: newPrice.toNumber(),
        cost: newCost.toNumber(),
        tax: newTax.toNumber(),
      },
    }).isDirty
  }, [
    desiredSupplies,
    isDraft,
    isDtoolsSiLocked,
    keepSuppliesUnchanged,
    lockedRates,
    quote,
    quoteDetails,
    suppliesQuoteDetail,
  ])

  const {
    isPending: isUpdatingSuppliesQuoteDetail,
    schedule,
    cancel,
  } = usePluginMutation({
    flushId: 'supplies',
    fingerprint: () => [
      quoteDetailsUpdatedAt,
      sourceQuoteDetailsUpdatedAt,
      summaryUpdatedAt,
      previousSuppliesDetailsUpdatedAt,
      quoteUpdatedAt,
    ],
    scopeKey: quote?.quoteid,
    mutationKey: ['plugin', 'supplies', 'update-supplies-quote-detail'],
    isReady: () =>
      !isUpdatingSuppliesPolicy && sourceQueriesLoaded && (!keepSuppliesUnchanged || lockedRates !== null),
    notReadyMessage:
      keepSuppliesUnchanged && (previousQuoteIsError || previousSuppliesDetailsIsError)
        ? 'Previous approved supplies could not be loaded. Refresh the quote and try again.'
        : sourceQueriesLoaded && keepSuppliesUnchanged && !lockedRates
        ? 'Supplies cannot stay unchanged exactly with the current discounted product and labor subtotal. Adjust eligible products or labor, or turn off Keep supplies unchanged.'
        : 'Supplies summary is still loading. Please try activating again in a moment.',
    isDirty,
    mutationFn: async () => {
      if (!quote || !quoteDetails || !desiredSupplies || !suppliesQuoteDetail) {
        throw new Error('No quote or supplies quote detail found')
      }

      if (keepSuppliesUnchanged && !lockedRates) {
        throw new Error('Supplies cannot stay unchanged exactly with the current product and labor subtotal.')
      }

      const newPrice = new Decimal(desiredSupplies.price).toDecimalPlaces(2)
      const newCost = new Decimal(desiredSupplies.cost).toDecimalPlaces(2)
      const newTax = getTaxDecimal(newPrice, 1)
      const syncPlan = getSuppliesSyncPlan({
        currentRates: {
          priceRate: quote.dsy_suppliespricerate,
          costRate: quote.dsy_suppliescostrate,
        },
        desiredRates: keepSuppliesUnchanged ? lockedRates : null,
        currentDetail: {
          price: suppliesQuoteDetail.priceperunit,
          cost: suppliesQuoteDetail.msdyn_costpriceperunit,
          costAmount: suppliesQuoteDetail.msdyn_costamount,
          tax: suppliesQuoteDetail.tax,
        },
        desiredDetail: {
          price: newPrice.toNumber(),
          cost: newCost.toNumber(),
          tax: newTax.toNumber(),
        },
      })

      if (syncPlan.ratesNeedUpdate && lockedRates) {
        await updateRecord('quotes', quote.quoteid, {
          dsy_suppliespricerate: lockedRates.priceRate,
          dsy_suppliescostrate: lockedRates.costRate,
        })
        queryClient.setQueryData<QuoteData>(QUERY_KEYS.quote(), (cachedQuote) =>
          cachedQuote
            ? {
                ...cachedQuote,
                dsy_suppliespricerate: lockedRates.priceRate,
                dsy_suppliescostrate: lockedRates.costRate,
              }
            : cachedQuote
        )
      }

      const duplicateSuppliesQuoteDetails = quoteDetails.filter(
        (detail) =>
          detail.productid.productid === PRODUCT_ID_MAP['SUPPLIES'] &&
          detail.quotedetailid !== suppliesQuoteDetail.quotedetailid
      )

      if (duplicateSuppliesQuoteDetails.length) {
        await deleteQuoteDetails(duplicateSuppliesQuoteDetails)
      }

      if (syncPlan.detailNeedsUpdate) {
        await updateQuoteDetail({
          quoteDetail: suppliesQuoteDetail,
          cpqFieldsBody: {},
          quoteDetailBody: {
            quantity: 1,
            priceperunit: newPrice.toNumber(),
            msdyn_costpriceperunit: newCost.toNumber(),
            msdyn_costamount: newCost.toNumber(),
            baseamount: newPrice.toNumber(),
            tax: newTax.toNumber(),
            extendedamount: newPrice.plus(newTax).toDecimalPlaces(2).toNumber(),
            msdyn_taxable: true,
          },
        })
      }
      return { ratesUpdated: syncPlan.ratesNeedUpdate }
    },
    onSuccess: ({ ratesUpdated }) => {
      addQueryKeys([
        ...(ratesUpdated ? [QUERY_KEYS.quote()] : []),
        QUERY_KEYS.quoteDetails(),
        QUERY_KEYS.quoteDetails({ onlySummaryDetails: 'all' }),
      ])
    },
    onError: (error) => {
      console.error('SuppliesPlugin: ~ error:', error)
    },
  })

  useEffect(() => {
    if (
      isUpdatingSuppliesQuoteDetail ||
      isUpdatingSuppliesPolicy ||
      !quote ||
      !quoteDetails ||
      !suppliesQuoteDetail ||
      !sourceQueriesLoaded ||
      !isDraft ||
      isDtoolsSiLocked
    )
      return
    schedule(3000)
    return cancel
  }, [
    summary,
    previousSupplies,
    sourceQueriesLoaded,
    isUpdatingSuppliesQuoteDetail,
    isUpdatingSuppliesPolicy,
    quote,
    quoteDetails,
    suppliesQuoteDetail,
    isDraft,
    isDtoolsSiLocked,
    schedule,
    cancel,
  ])

  return null
})
